// Global currency formatting utility
// Uses the selected record's currency from Storage context

import { useAppContext } from '../contexts/Storage';
import currencyUtils, { codeToSymbol, symbolToCode } from './currencyUtils';

/**
 * Format a number as currency using the selected record's currency symbol
 * @param amount The amount to format
 * @param currencySymbol Optional: override the currency symbol (defaults to selected record's currency)
 * @param decimals Number of decimal places (default: 2)
 * @returns Formatted currency string (e.g., "$1,234.56", "₦50,000")
 */
export function formatCurrency(
  amount: number, 
  currencySymbol?: string, 
  decimals: number = 2
): string {
  // Format number with thousands separators and decimals
  const formattedNumber = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Math.abs(amount));
  
  // Get the sign for negative numbers
  const sign = amount < 0 ? '-' : '';
  
  // Use provided symbol or default to empty string
  const symbol = currencySymbol || '';
  
  return `${sign}${symbol}${formattedNumber}`;
}

/**
 * Format a number as currency with no decimal places (for whole numbers)
 * @param amount The amount to format
 * @param currencySymbol Optional: override the currency symbol
 * @returns Formatted currency string without decimals (e.g., "$1,234", "₦50,000")
 */
export function formatCurrencyWhole(
  amount: number, 
  currencySymbol?: string
): string {
  return formatCurrency(amount, currencySymbol, 0);
}

/**
 * Hook to get currency formatting functions with the selected record's currency
 * @returns Object with formatting functions using current record's currency
 */
export function useCurrency() {
  const { selectedRecord } = useAppContext();
  
  // Get currency symbol from selected record, fallback to $
  const getCurrencySymbol = (): string => {
    if (selectedRecord?.record_currency_code) {
      // The record_currency_code might be a symbol (₦) or code (NGN)
      const currency = selectedRecord.record_currency_code;
      
      // If it's a code, convert to symbol
      if (currencyUtils.isCurrencyCode(currency)) {
        return codeToSymbol(currency);
      }
      
      // If it's already a symbol, use it directly
      if (currencyUtils.isCurrencySymbol(currency)) {
        return currency;
      }
      
      // Otherwise, assume it's a symbol
      return currency;
    }
    
    return ''; // Default fallback
  };
  
  const currencySymbol = getCurrencySymbol();
  
  return {
    /**
     * Format amount using selected record's currency
     */
    format: (amount: number, decimals: number = 2) => 
      formatCurrency(amount, currencySymbol, decimals),
    
    /**
     * Format amount as whole number using selected record's currency
     */
    formatWhole: (amount: number) => 
      formatCurrencyWhole(amount, currencySymbol),
    
    /**
     * Get the current currency symbol
     */
    getSymbol: () => currencySymbol,
    
    /**
     * Get the current currency code (if available)
     */
    getCode: (): string | undefined => {
      if (selectedRecord?.record_currency_code) {
        const currency = selectedRecord.record_currency_code;
        
        // If it's a symbol, try to convert to code
        if (currencyUtils.isCurrencySymbol(currency)) {
          return symbolToCode(currency);
        }
        
        // If it's already a code, use it
        if (currencyUtils.isCurrencyCode(currency)) {
          return currency;
        }
      }
      
      return undefined;
    },
    
    /**
     * Check if a record is selected
     */
    hasSelectedRecord: !!selectedRecord,
    
    /**
     * Get the selected record (for advanced use cases)
     */
    selectedRecord,
  };
}

/**
 * Utility function for components that can't use hooks
 * @param currencySymbol The currency symbol to use
 * @returns Object with formatting functions
 */
export function createCurrencyFormatter(currencySymbol: string = '') {
  return {
    format: (amount: number, decimals: number = 2) => 
      formatCurrency(amount, currencySymbol, decimals),
    
    formatWhole: (amount: number) => 
      formatCurrencyWhole(amount, currencySymbol),
    
    getSymbol: () => currencySymbol,
  };
}

// Default export with all utilities
const globalCurrency = {
  formatCurrency,
  formatCurrencyWhole,
  useCurrency,
  createCurrencyFormatter,
};

export default globalCurrency;